import { Component, ElementRef, computed, effect, inject, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-mobile-drawer',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslocoPipe],
  templateUrl: './mobile-drawer.component.html',
  styleUrl: './mobile-drawer.component.scss',
})
export class MobileDrawerComponent {
  protected readonly authService = inject(AuthService);
  private readonly elementRef = inject(ElementRef);

  readonly open = input.required<boolean>();
  readonly closeRequested = output<void>();

  readonly isCoach = computed(() => this.authService.userProfile()?.role === 'coach');

  constructor() {
    // Focus the first focusable element when the drawer opens so keyboard/screen-reader
    // users land inside the drawer rather than remaining behind the backdrop.
    effect(() => {
      if (this.open()) {
        setTimeout(() => this.focusFirst(), 0);
      }
    });
  }

  onNavClick(): void {
    this.closeRequested.emit();
  }

  onBackdropClick(): void {
    this.closeRequested.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeRequested.emit();
      return;
    }
    if (event.key === 'Tab') {
      this.trapTab(event);
    }
  }

  private focusFirst(): void {
    this.getFocusable()[0]?.focus();
  }

  private trapTab(event: KeyboardEvent): void {
    const items = this.getFocusable();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  private getFocusable(): HTMLElement[] {
    return Array.from(
      (this.elementRef.nativeElement as HTMLElement).querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }
}
